/*
Copyright (c) 2025 Mathilde Meyer, Naziha Beghdadi
Licensed under the MIT License (see LICENSE.txt file for details)
*/

import React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  Tooltip,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

// ── Shared helpers (imported by Section36Form, DocPreview, docxGenerator) ──

export const DEFAULT_STRAIN_COLUMNS = [
  { id: "wt", label: "WT" },
  { id: "gmStrain1", label: "GM Strain 1" },
  { id: "gmStrain2", label: "GM Strain 2" },
];

export const getStrainColumns = (data) =>
  data[0]?.strainColumns?.length > 0 ? data[0].strainColumns : DEFAULT_STRAIN_COLUMNS;

// Backward-compatible count reader: prefers group.counts[id], falls back to old flat fields
export const getGroupCount = (group, colId) => {
  if (group.counts && group.counts[colId] !== undefined) return group.counts[colId];
  if (colId === "wt") return group.wtCount || "";
  if (colId === "gmStrain1") return group.gmStrain1Count || "";
  if (colId === "gmStrain2") return group.gmStrain2Count || "";
  return "";
};

// Build a counts object from a group (migrating old flat fields if needed)
const toCountsObj = (group, columns) => {
  if (group.counts) return group.counts;
  const counts = {};
  columns.forEach(({ id }) => { counts[id] = getGroupCount(group, id); });
  return counts;
};

// ─────────────────────────────────────────────────────────────────────────────

function Section29Form({ index, data, setData }) {
  const exp = data[index];
  const strainColumns = getStrainColumns(data);

  const objectiveOptions = Array.from(
    new Set(data.map((entry) => entry.objectiveLabel?.trim()).filter(Boolean))
  );

  const update = (field, value) => {
    const newData = [...data];
    newData[index] = { ...newData[index], [field]: value };
    setData(newData);
  };

  // ── Column management (propagated to all experiments) ──

  const updateColumnLabel = (colIndex, label) => {
    const updatedId = strainColumns[colIndex].id;
    const newData = data.map((exp) => {
      const expCols = exp.strainColumns?.length > 0 ? exp.strainColumns : DEFAULT_STRAIN_COLUMNS;
      return { ...exp, strainColumns: expCols.map((c) => c.id === updatedId ? { ...c, label } : c) };
    });
    setData(newData);
  };

  const addColumn = () => {
    const newId = `col_${Date.now()}`;
    const newLabel = `Strain ${strainColumns.length + 1}`;
    const newData = data.map((exp) => {
      const expCols = exp.strainColumns?.length > 0 ? exp.strainColumns : DEFAULT_STRAIN_COLUMNS;
      const cols = [...expCols, { id: newId, label: newLabel }];
      const groups = (exp.groups || []).map((g) => ({
        ...g,
        counts: { ...toCountsObj(g, expCols), [newId]: "" },
      }));
      return { ...exp, strainColumns: cols, groups };
    });
    setData(newData);
  };

  const removeColumn = (colIndex) => {
    if (strainColumns.length <= 1) return;
    const removedId = strainColumns[colIndex].id;
    const newData = data.map((exp) => {
      const expCols = exp.strainColumns?.length > 0 ? exp.strainColumns : DEFAULT_STRAIN_COLUMNS;
      const cols = expCols.filter((c) => c.id !== removedId);
      const groups = (exp.groups || []).map((g) => {
        const counts = { ...toCountsObj(g, expCols) };
        delete counts[removedId];
        return { ...g, counts };
      });
      const dist = { ...(exp.severityDistribution || {}) };
      Object.keys(dist).forEach((sdKey) => {
        const row = { ...(dist[sdKey] || {}) };
        delete row[removedId];
        dist[sdKey] = row;
      });
      return { ...exp, strainColumns: cols, groups, severityDistribution: dist };
    });
    setData(newData);
  };

  // ── Group management ──

  const updateGroupCount = (groupIndex, colId, value) => {
    const newData = [...data];
    const groups = [...(newData[index].groups || [])];
    const counts = { ...toCountsObj(groups[groupIndex], strainColumns), [colId]: value };
    groups[groupIndex] = { ...groups[groupIndex], counts };
    newData[index] = { ...newData[index], groups };
    setData(newData);
  };

  const updateGroupName = (groupIndex, value) => {
    const newData = [...data];
    const groups = [...(newData[index].groups || [])];
    groups[groupIndex] = { ...groups[groupIndex], groupName: value };
    newData[index] = { ...newData[index], groups };
    setData(newData);
  };

  const addGroup = () => {
    const newData = [...data];
    const groups = [...(newData[index].groups || [])];
    const counts = {};
    strainColumns.forEach(({ id }) => { counts[id] = ""; });
    groups.push({ groupName: "", counts });
    newData[index] = { ...newData[index], groups };
    setData(newData);
  };

  const removeGroup = (groupIndex) => {
    const newData = [...data];
    const groups = [...(newData[index].groups || [])];
    if (groups.length > 1) {
      groups.splice(groupIndex, 1);
      newData[index] = { ...newData[index], groups };
      setData(newData);
    }
  };

  const groups = exp.groups || [];
  const colTotals = strainColumns.map(({ id }) =>
    groups.reduce((sum, g) => sum + (parseInt(getGroupCount(g, id), 10) || 0), 0)
  );
  const totalAll = colTotals.reduce((a, b) => a + b, 0);

  return (
    <Box sx={{ mt: 4, p: 3, border: "1px solid #ccc", borderRadius: 2, backgroundColor: "#fafafa" }}>
      <Box sx={{ mb: 2, px: 2, py: 1, borderLeft: "4px solid", borderColor: "primary.main", backgroundColor: "primary.50" }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.main" }}>
          Section 29 — Experimental Set-up and Study Design
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Use this section to enter the groups and number of animals you will need for this experiment. The counts
        entered here will generate a table with the number of animals to request in section 9 and to justify in
        section 30. The total is used later in section 36 too.
      </Typography>

      <Autocomplete
        freeSolo
        options={objectiveOptions}
        value={exp.objectiveLabel || ""}
        onInputChange={(_, value) => update("objectiveLabel", value)}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            label="Objective label (e.g., Objective 1)"
            helperText="Reuse the same objective label on multiple experiments when they belong to the same objective."
            sx={{ mb: 2 }}
            size="small"
          />
        )}
      />

      <TextField
        fullWidth
        label="Experiment label / objective"
        value={exp.experimentObjective || ""}
        onChange={(e) => update("experimentObjective", e.target.value)}
        helperText="Set in Section 23 (Step 1). You can edit it here — changes apply everywhere."
        sx={{ mb: 2 }}
        size="small"
      />

      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Experimental design description"
        placeholder="Describe the overall design: strains, GM lines, controls, procedures, end of experiment, in vivo or postmortem analysis, etc..."
        value={exp.experimentalDesign || ""}
        onChange={(e) => update("experimentalDesign", e.target.value)}
        helperText="Briefly explain the aim of this experiment, how it is conducted, and the relevance of each proposed group."
        sx={{ mb: 2 }}
        size="small"
      />

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "medium" }}>
        Animal Groups
      </Typography>

      {/* WT / GM explanation box */}
      <Box sx={{ mb: 2, p: 2, borderRadius: 1.5, border: "1px solid #e3eaf0", backgroundColor: "#f7fbfd", fontSize: "0.85rem", lineHeight: 1.65 }}>
        <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.75 }}>
          Column definitions
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>WT — Wild-type animals:</strong> Animals that have not been genetically modified.
          Mutant animals, without any phenotype, are considered as WT. This includes standard inbred
          or outbred strains (e.g. C57BL/6, Wistar rat) as well as wild-type littermates (+/+) used
          as controls in experiments involving a GM line.
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>GM Strain 1 / GM Strain 2 — Genetically modified animals:</strong> Any animal
          carrying a genetically induced genetic modification, regardless of whether it shows a visible
          phenotype. This includes transgenic lines, knock-out or knock-in models. Spontaneous
          mutations with no additional genetic modifications should be reported as WT.
        </Typography>
        <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #dce8ef", display: "flex", flexDirection: "column", gap: 0.4 }}>
          <Typography variant="body2" color="text.secondary">
            Each GM line or line carrying a spontaneous mutation used in your project requires a <strong>Form D</strong> to
            be attached to section 9. If any line shows a constrained phenotype (e.g. reduced mobility,
            reduced reproductive capacity, development of a disease...), a <strong>Form M</strong> must
            also be submitted. Contact the animal facility to prepare Forms D and M.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Animals assigned to one experiment cannot be reused in another experiment.</strong>
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Add one row per condition. Example: control vs treatment are two separate rows. In the column, describe groups in each condition. You can rename, add, or remove columns using the column headers below.
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: "bold" }}>Group Name</TableCell>
              {strainColumns.map((col, ci) => (
                <TableCell key={col.id} align="center" sx={{ minWidth: 110 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25 }}>
                    <TextField
                      size="small"
                      value={col.label}
                      onChange={(e) => updateColumnLabel(ci, e.target.value)}
                      variant="standard"
                      inputProps={{ style: { textAlign: "center", fontWeight: "bold", fontSize: "0.875rem" } }}
                      sx={{ width: 80 }}
                    />
                    <Tooltip title={strainColumns.length <= 1 ? "At least one column required" : "Remove column"}>
                      <span>
                        <IconButton size="small" onClick={() => removeColumn(ci)} disabled={strainColumns.length <= 1}>
                          <DeleteOutlineIcon fontSize="small" color={strainColumns.length <= 1 ? "disabled" : "error"} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Total</TableCell>
              <TableCell align="center">
                <Tooltip title="Add strain column">
                  <IconButton size="small" onClick={addColumn} color="primary">
                    <AddCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.length === 0 && (
              <TableRow>
                <TableCell colSpan={strainColumns.length + 3} align="center" sx={{ color: "text.secondary", fontStyle: "italic", py: 2 }}>
                  No groups added yet. Click "Add Group" below.
                </TableCell>
              </TableRow>
            )}
            {groups.map((group, gi) => {
              const rowTotal = strainColumns.reduce(
                (sum, { id }) => sum + (parseInt(getGroupCount(group, id), 10) || 0), 0
              );
              return (
                <TableRow key={gi}>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="e.g. Control Group"
                      value={group.groupName || ""}
                      onChange={(e) => updateGroupName(gi, e.target.value)}
                      variant="standard"
                      fullWidth
                    />
                  </TableCell>
                  {strainColumns.map(({ id }) => (
                    <TableCell key={id} align="center">
                      <TextField
                        size="small"
                        type="number"
                        value={getGroupCount(group, id)}
                        onChange={(e) => updateGroupCount(gi, id, e.target.value)}
                        variant="standard"
                        inputProps={{ min: 0, style: { textAlign: "center", width: "60px" } }}
                      />
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>{rowTotal}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => removeGroup(gi)} disabled={groups.length <= 1}>
                      <DeleteOutlineIcon fontSize="small" color={groups.length <= 1 ? "disabled" : "error"} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {groups.length > 0 && (
              <TableRow sx={{ backgroundColor: "#fce4ec" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Totals</TableCell>
                {colTotals.map((total, ci) => (
                  <TableCell key={strainColumns[ci].id} align="center" sx={{ fontWeight: "bold" }}>{total}</TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight: "bold" }}>{totalAll}</TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Button variant="outlined" size="small" startIcon={<AddCircleOutlineIcon />} onClick={addGroup} sx={{ mb: 3 }}>
        Add Group
      </Button>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: "medium" }}>
        Study Design Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Complete these fields so the reader understands how animals are assigned, how bias is reduced, and how the
        results will be analyzed.
      </Typography>

      <TextField fullWidth label="Randomisation / Assignment" placeholder="Describe how animals are assigned to groups..." value={exp.randomization || ""} onChange={(e) => update("randomization", e.target.value)} sx={{ mb: 2 }} size="small" />
      <TextField fullWidth label="Blinding" placeholder="Describe if and how blinding is applied..." value={exp.blinding || ""} onChange={(e) => update("blinding", e.target.value)} sx={{ mb: 2 }} size="small" />
      <TextField fullWidth label="Inclusion criteria" value={exp.inclusionCriteria || ""} onChange={(e) => update("inclusionCriteria", e.target.value)} sx={{ mb: 2 }} size="small" />
      <TextField fullWidth label="Exclusion criteria" value={exp.exclusionCriteria || ""} onChange={(e) => update("exclusionCriteria", e.target.value)} sx={{ mb: 2 }} size="small" />
      <TextField fullWidth label="Primary endpoints" value={exp.primaryEndpoints || ""} onChange={(e) => update("primaryEndpoints", e.target.value)} sx={{ mb: 2 }} size="small" />
      <TextField fullWidth multiline minRows={2} label="Statistical analysis plan" placeholder="Describe the planned statistical methods..." value={exp.statisticalAnalysis || ""} onChange={(e) => update("statisticalAnalysis", e.target.value)} sx={{ mb: 2 }} size="small" />

      <Divider sx={{ mb: 2 }} />

      <Alert severity="info" sx={{ mb: 0 }}>
        Animal counts entered above are used in Section 36 and the document summary. To justify
        these numbers with statistical reasoning, proceed to <strong>Step 3 — Section 30</strong>,
        which contains the guided questionnaire.
      </Alert>
    </Box>
  );
}

export default Section29Form;
